import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend
} from 'recharts';
import {
  FaTimes, FaSpinner, FaSync, FaEthernet, FaNetworkWired,
  FaArrowUp, FaArrowDown, FaExclamationTriangle, FaCheckCircle,
  FaTimesCircle, FaGlobe, FaInfoCircle, FaTachometerAlt,
  FaChartLine, FaCog, FaSignal, FaBolt, FaTools, FaTrafficLight,
  FaUndo
} from 'react-icons/fa';
import './InterfaceDetailModal.scss';
import * as Sentry from '@sentry/react';

// =====================================================================
// Auto-refresh interval for live traffic monitoring (milliseconds).
// 5 seconds matches MikroTik WinBox default chart refresh rate.
// =====================================================================
const AUTO_REFRESH_INTERVAL_MS = 5000;

// Maximum number of traffic history data points kept for live charts.
// 60 points × 5s = 5 minutes of rolling history.
const MAX_CHART_POINTS = 60;

// Duration (in milliseconds) to display success messages in the Actions tab
// before automatically clearing them.
const SUCCESS_MESSAGE_CLEAR_MS = 3000;

// =====================================================================
// Ethernet-specific properties displayed in the dedicated Status tab
// (matching MikroTik WinBox "Status" tab). These are filtered out of
// the generic Config tab to avoid duplication.
// =====================================================================
const ETHERNET_STATUS_KEYS = new Set([
  'rate', 'full-duplex', 'auto-negotiation', 'fec',
  'advertise', 'link-partner-advertise', 'sfp-rate-select',
  'loop-protect', 'loop-protect-status',
  'loop-protect-send-interval', 'loop-protect-disable-time'
]);

// =====================================================================
// Helper: formats a raw byte count into a human-readable string.
// =====================================================================
const formatBytes = (bytesString) => {
  const bytes = parseInt(bytesString, 10);
  if (isNaN(bytes) || bytes === 0) return '0 B';
  const absoluteBytes = Math.abs(bytes);
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unitIndex = Math.min(Math.floor(Math.log(absoluteBytes) / Math.log(1024)), units.length - 1);
  const value = absoluteBytes / Math.pow(1024, unitIndex);
  return `${value.toFixed(unitIndex > 0 ? 2 : 0)} ${units[unitIndex]}`;
};

// =====================================================================
// Helper: formats a raw bits-per-second value into a rate string.
// =====================================================================
const formatBitsPerSecond = (bpsString) => {
  const bps = parseInt(bpsString, 10);
  if (isNaN(bps) || bps === 0) return '0 bps';
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps'];
  const unitIndex = Math.min(Math.floor(Math.log(bps) / Math.log(1000)), units.length - 1);
  const value = bps / Math.pow(1000, unitIndex);
  return `${value.toFixed(unitIndex > 0 ? 2 : 0)} ${units[unitIndex]}`;
};

// =====================================================================
// Helper: formats a large number with thousands separators.
// =====================================================================
const formatNumber = (numString) => {
  const num = parseInt(numString, 10);
  if (isNaN(num)) return '0';
  return num.toLocaleString();
};

// =====================================================================
// Mapping of MikroTik property keys to human-readable labels.
// =====================================================================
const propertyLabelMap = {
  'speed': 'Speed', 'rate': 'Rate', 'full-duplex': 'Full Duplex',
  'auto-negotiation': 'Auto Negotiation', 'advertise': 'Advertising',
  'link-partner-advertise': 'Link Partner Advertising', 'fec': 'FEC',
  'bandwidth': 'Bandwidth', 'loop-protect': 'Loop Protect',
  'loop-protect-status': 'Loop Protect Status',
  'loop-protect-send-interval': 'Loop Protect Send Interval',
  'loop-protect-disable-time': 'Loop Protect Disable Time',
  'arp': 'ARP', 'arp-timeout': 'ARP Timeout',
  'rx-flow-control': 'RX Flow Control', 'tx-flow-control': 'TX Flow Control',
  'orig-mac-address': 'Original MAC Address',
  'poe-out': 'PoE Out', 'poe-priority': 'PoE Priority',
  'combo-mode': 'Combo Mode', 'sfp-rate-select': 'SFP Rate Select',
  'sfp-shutdown-temperature': 'SFP Shutdown Temperature',
  'vlan-id': 'VLAN ID', 'interface': 'Interface',
  'remote-address': 'Remote Address', 'local-address': 'Local Address',
  'tunnel-id': 'Tunnel ID', 'keepalive': 'Keep Alive',
  'clamp-tcp-mss': 'Clamp TCP MSS', 'dont-fragment': "Don't Fragment",
  'allow-fast-path': 'Allow Fast Path',
  'listen-port': 'Listen Port', 'public-key': 'Public Key',
  'protocol-mode': 'Protocol Mode', 'fast-forward': 'Fast Forward',
  'igmp-snooping': 'IGMP Snooping', 'vlan-filtering': 'VLAN Filtering',
  'dhcp-snooping': 'DHCP Snooping', 'service-name': 'Service Name',
  'ac-name': 'AC Name', 'max-mtu': 'Max MTU', 'max-mru': 'Max MRU',
  'user': 'User', 'profile': 'Profile',
  'keepalive-timeout': 'Keepalive Timeout',
  'use-peer-dns': 'Use Peer DNS', 'add-default-route': 'Add Default Route',
  'default-route-distance': 'Default Route Distance',
  'dial-on-demand': 'Dial on Demand',
  'switch': 'Switch', 'slave': 'Slave'
};

// =====================================================================
// Helper: converts a RouterOS key into a display label.
// =====================================================================
const formatPropertyLabel = (key) =>
  propertyLabelMap[key] || key.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// =====================================================================
// Properties hidden from the Config tab because they are shown in
// General or Status sections, or contain sensitive data.
// =====================================================================
const hiddenConfigProperties = new Set([
  'name', 'default-name', 'mtu', 'actual-mtu', 'l2mtu', 'max-l2mtu',
  'mac-address', 'comment', 'running', 'disabled', 'slave',
  'tx-byte', 'rx-byte', 'tx-packet', 'rx-packet', 'tx-drop', 'rx-drop',
  'tx-error', 'rx-error', 'tx-queue-drop',
  'fp-tx-byte', 'fp-rx-byte', 'fp-tx-packet', 'fp-rx-packet',
  'private-key', 'password', 'ipsec-secret',
  ...ETHERNET_STATUS_KEYS
]);

// =====================================================================
// Custom Recharts tooltip for traffic charts — shows formatted values.
// =====================================================================
const TrafficChartTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ifd-chart-tooltip">
      <div className="ifd-chart-tooltip-time">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="ifd-chart-tooltip-row" style={{ color: entry.color }}>
          {entry.name}: {unit === 'bps'
            ? formatBitsPerSecond(entry.value)
            : `${formatNumber(entry.value)} pps`}
        </div>
      ))}
    </div>
  );
};

/**
 * InterfaceDetailModal — a MikroTik WinBox-inspired interface detail dialog
 * showing General, Status (Ethernet link), Traffic (live charts + counters),
 * Config (type-specific), and Actions tabs.
 *
 * Props:
 *   routerId       — numeric DB ID of the router configuration
 *   interfaceName  — the RouterOS interface name to query
 *   onClose        — callback to close the modal
 */
const InterfaceDetailModal = ({ routerId, interfaceName, onClose }) => {
  const { apiFetch } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [refreshing, setRefreshing] = useState(false);
  const autoRefreshRef = useRef(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Rolling buffer of traffic rate samples for live charts.
  // Each entry: { time, txBps, rxBps, txPps, rxPps }
  const [trafficHistory, setTrafficHistory] = useState([]);

  // Action feedback state — tracks per-action loading and result messages.
  const [actionState, setActionState] = useState({});

  // Cable test results (key-value pairs from the RouterOS response).
  const [cableTestResults, setCableTestResults] = useState(null);

  // Fetch comprehensive interface detail from the backend.
  const fetchDetail = useCallback(async (isAutoRefresh = false) => {
    if (!isAutoRefresh) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      const encodedName = encodeURIComponent(interfaceName);
      const response = await apiFetch(
        `/api/RouterConfig/${routerId}/interfaces/${encodedName}/detail`
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to fetch interface details');
        return;
      }

      const d = data.data;
      setDetail(d);

      // Append a new data point to the rolling traffic history chart buffer.
      const now = new Date();
      const timeLabel = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setTrafficHistory(prev => {
        const newPoint = {
          time: timeLabel,
          txBps: parseInt(d.txBitsPerSecond, 10) || 0,
          rxBps: parseInt(d.rxBitsPerSecond, 10) || 0,
          txPps: parseInt(d.txPacketsPerSecond, 10) || 0,
          rxPps: parseInt(d.rxPacketsPerSecond, 10) || 0
        };
        return [...prev.slice(-(MAX_CHART_POINTS - 1)), newPoint];
      });
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [routerId, interfaceName, apiFetch]);

  // Initial fetch on mount.
  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // Auto-refresh interval for live traffic monitoring.
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => fetchDetail(true), AUTO_REFRESH_INTERVAL_MS);
    }
    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
    };
  }, [autoRefresh, fetchDetail]);

  // Close on Escape key press.
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // =====================================================================
  // Perform a router action (reset-counters, blink, cable-test)
  // =====================================================================
  const performAction = useCallback(async (actionKey, apiPath) => {
    setActionState(prev => ({ ...prev, [actionKey]: { loading: true, message: null, error: null } }));
    try {
      const encodedName = encodeURIComponent(interfaceName);
      const response = await apiFetch(
        `/api/RouterConfig/${routerId}/interfaces/${encodedName}/${apiPath}`,
        { method: 'POST' }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setActionState(prev => ({
          ...prev,
          [actionKey]: { loading: false, error: data.error || 'Action failed', message: null }
        }));
        return;
      }

      // For cable test, store the results for display.
      if (actionKey === 'cableTest' && data.data) {
        setCableTestResults(data.data);
      }

      // After reset-counters, refresh the detail to show zeroed values.
      if (actionKey === 'resetCounters') {
        await fetchDetail(true);
      }

      setActionState(prev => ({
        ...prev,
        [actionKey]: { loading: false, message: 'Done', error: null }
      }));

      // Clear the success message after the configured timeout.
      setTimeout(() => {
        setActionState(prev => ({ ...prev, [actionKey]: { loading: false, message: null, error: null } }));
      }, SUCCESS_MESSAGE_CLEAR_MS);
    } catch (err) {
        Sentry.captureException(err);
      setActionState(prev => ({
        ...prev,
        [actionKey]: { loading: false, error: err.message || 'Network error', message: null }
      }));
    }
  }, [routerId, interfaceName, apiFetch, fetchDetail]);

  // Determine if this is an Ethernet interface (enables Ethernet-specific actions + Status tab).
  const isEthernet = detail?.type?.toLowerCase() === 'ether';

  // Build the Ethernet status properties for the Status tab from TypeSpecificProperties.
  const ethernetStatusEntries = (detail?.typeSpecificProperties && isEthernet)
    ? Object.entries(detail.typeSpecificProperties)
        .filter(([key]) => ETHERNET_STATUS_KEYS.has(key))
        .sort((a, b) => {
          // Sort by preferred order matching MikroTik WinBox layout.
          const order = ['rate', 'full-duplex', 'auto-negotiation', 'fec', 'advertise', 'link-partner-advertise'];
          const aIdx = order.indexOf(a[0]);
          const bIdx = order.indexOf(b[0]);
          if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
          if (aIdx >= 0) return -1;
          if (bIdx >= 0) return 1;
          return a[0].localeCompare(b[0]);
        })
    : [];

  // Config tab entries — everything not shown in General/Status.
  const configEntries = detail?.typeSpecificProperties
    ? Object.entries(detail.typeSpecificProperties)
        .filter(([key]) => !hiddenConfigProperties.has(key))
        .sort((a, b) => a[0].localeCompare(b[0]))
    : [];

  // Build the tab list dynamically based on available data.
  const tabs = [
    { key: 'general', label: 'General', icon: <FaInfoCircle /> },
    ...(isEthernet || detail?.lastLinkUpTime || detail?.lastLinkDownTime
      ? [{ key: 'status', label: 'Status', icon: <FaSignal /> }]
      : []),
    { key: 'traffic', label: 'Traffic', icon: <FaChartLine /> },
    ...(configEntries.length > 0
      ? [{
          key: 'config',
          label: detail?.type ? `${detail.type.charAt(0).toUpperCase() + detail.type.slice(1)} Config` : 'Config',
          icon: <FaCog />
        }]
      : []),
    { key: 'actions', label: 'Actions', icon: <FaTools /> }
  ];

  return (
    <div className="ifd-modal-overlay" onClick={e => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="ifd-modal">
        {/* Modal header */}
        <div className="ifd-modal-header">
          <div className="ifd-modal-title">
            <FaEthernet className="ifd-modal-title-icon" />
            <span>{interfaceName}</span>
            {detail && (
              <span className={`ifd-status-pill ${detail.running ? 'ifd-status-pill--running' : detail.disabled ? 'ifd-status-pill--disabled' : 'ifd-status-pill--down'}`}>
                {detail.running ? 'Running' : detail.disabled ? 'Disabled' : 'Down'}
              </span>
            )}
            {detail?.type && <span className="ifd-type-badge">{detail.type}</span>}
          </div>
          <div className="ifd-modal-header-actions">
            <label className="ifd-auto-refresh-toggle" title={`Auto-refresh every ${AUTO_REFRESH_INTERVAL_MS / 1000}s`}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={e => setAutoRefresh(e.target.checked)}
              />
              Auto
            </label>
            <button className="ifd-btn-refresh" onClick={() => fetchDetail(false)} disabled={loading} title="Refresh data">
              {refreshing ? <FaSpinner className="spin" /> : <FaSync />}
            </button>
            <button className="ifd-btn-close" onClick={onClose} title="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="ifd-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`ifd-tab ${activeTab === tab.key ? 'ifd-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Modal body */}
        <div className="ifd-modal-body">
          {loading && !detail ? (
            <div className="ifd-loading">
              <FaSpinner className="spin" />
              <span>Loading interface data…</span>
            </div>
          ) : error ? (
            <div className="ifd-error">
              <FaExclamationTriangle />
              <span>{error}</span>
              <button className="ifd-btn-retry" onClick={() => fetchDetail()}>Retry</button>
            </div>
          ) : detail ? (
            <>
              {activeTab === 'general' && <GeneralTab detail={detail} />}
              {activeTab === 'status' && <StatusTab detail={detail} ethernetStatusEntries={ethernetStatusEntries} />}
              {activeTab === 'traffic' && <TrafficTab detail={detail} trafficHistory={trafficHistory} />}
              {activeTab === 'config' && <ConfigTab entries={configEntries} />}
              {activeTab === 'actions' && (
                <ActionsTab
                  interfaceType={detail.type}
                  isEthernet={isEthernet}
                  actionState={actionState}
                  cableTestResults={cableTestResults}
                  onResetCounters={() => performAction('resetCounters', 'reset-counters')}
                  onBlink={() => performAction('blink', 'blink')}
                  onCableTest={() => performAction('cableTest', 'cable-test')}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// GeneralTab — core interface properties and assigned IP addresses.
// Mirrors the MikroTik WinBox "General" tab.
// =====================================================================
const GeneralTab = ({ detail }) => (
  <div className="ifd-tab-content">
    <div className="ifd-section">
      <h4 className="ifd-section-title"><FaInfoCircle /> General Information</h4>
      <div className="ifd-props-grid">
        <PropRow label="Name" value={detail.name} />
        {detail.defaultName && <PropRow label="Default Name" value={detail.defaultName} />}
        <PropRow label="Type" value={detail.type} />
        <PropRow label="MAC Address" value={detail.macAddress} mono />
        <PropRow label="MTU" value={detail.mtu || 'auto'} />
        {detail.actualMtu && <PropRow label="Actual MTU" value={detail.actualMtu} />}
        {detail.l2Mtu && <PropRow label="L2 MTU" value={detail.l2Mtu} />}
        {detail.maxL2Mtu && <PropRow label="Max L2 MTU" value={detail.maxL2Mtu} />}
        {detail.vrf && <PropRow label="VRF" value={detail.vrf} />}
        {detail.arp && <PropRow label="ARP" value={detail.arp} />}
        {detail.arpTimeout && <PropRow label="ARP Timeout" value={detail.arpTimeout} />}
        <PropRow label="Status" value={
          detail.running
            ? <span className="ifd-value--running"><FaCheckCircle /> Running</span>
            : detail.disabled
              ? <span className="ifd-value--disabled"><FaTimesCircle /> Disabled</span>
              : <span className="ifd-value--down"><FaExclamationTriangle /> Down</span>
        } />
        {detail.slave && <PropRow label="Slave" value="Yes" />}
        {detail.comment && <PropRow label="Comment" value={detail.comment} />}
      </div>
    </div>

    {detail.addresses && detail.addresses.length > 0 && (
      <div className="ifd-section">
        <h4 className="ifd-section-title"><FaGlobe /> IP Addresses</h4>
        <div className="ifd-ip-table">
          <div className="ifd-ip-header">
            <span>Address</span>
            <span>Network</span>
            <span>Status</span>
          </div>
          {detail.addresses.map((addr, index) => (
            <div key={addr.id || index} className="ifd-ip-row">
              <span className="mono">{addr.address}</span>
              <span className="mono">{addr.network}</span>
              <span>
                {addr.disabled
                  ? <span className="ifd-value--disabled">Disabled</span>
                  : addr.dynamic
                    ? <span className="ifd-value--dynamic">Dynamic</span>
                    : <span className="ifd-value--static">Static</span>
                }
              </span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// =====================================================================
// StatusTab — link status information matching MikroTik WinBox "Status".
// Shows last link up/down times, link downs counter, and Ethernet-
// specific negotiation results (rate, full-duplex, auto-negotiation, FEC).
// =====================================================================
const StatusTab = ({ detail, ethernetStatusEntries }) => (
  <div className="ifd-tab-content">
    <div className="ifd-section">
      <h4 className="ifd-section-title"><FaSignal /> Link Status</h4>
      <div className="ifd-props-grid">
        {detail.lastLinkUpTime && <PropRow label="Last Link Up" value={detail.lastLinkUpTime} />}
        {detail.lastLinkDownTime && <PropRow label="Last Link Down" value={detail.lastLinkDownTime} />}
        <PropRow label="Link Downs" value={detail.linkDowns || '0'} />
      </div>
    </div>

    {ethernetStatusEntries.length > 0 && (
      <div className="ifd-section">
        <h4 className="ifd-section-title"><FaEthernet /> Ethernet Negotiation</h4>
        <div className="ifd-props-grid">
          {ethernetStatusEntries.map(([key, value]) => (
            <PropRow
              key={key}
              label={formatPropertyLabel(key)}
              value={formatPropertyValue(key, value)}
              mono={isMonoValue(key)}
            />
          ))}
        </div>
      </div>
    )}
  </div>
);

// =====================================================================
// TrafficTab — live rolling charts plus cumulative and fast-path counters.
// The charts use Recharts LineChart with a rolling 60-point history buffer.
// =====================================================================
const TrafficTab = ({ detail, trafficHistory }) => (
  <div className="ifd-tab-content">
    {/* Live Rate Cards */}
    <div className="ifd-section">
      <h4 className="ifd-section-title"><FaTachometerAlt /> Live Traffic Rates</h4>
      <div className="ifd-traffic-grid">
        <div className="ifd-traffic-card ifd-traffic-card--tx">
          <div className="ifd-traffic-card-header"><FaArrowUp /> TX (Upload)</div>
          <div className="ifd-traffic-card-value">{formatBitsPerSecond(detail.txBitsPerSecond)}</div>
          <div className="ifd-traffic-card-sub">{formatNumber(detail.txPacketsPerSecond)} pps</div>
        </div>
        <div className="ifd-traffic-card ifd-traffic-card--rx">
          <div className="ifd-traffic-card-header"><FaArrowDown /> RX (Download)</div>
          <div className="ifd-traffic-card-value">{formatBitsPerSecond(detail.rxBitsPerSecond)}</div>
          <div className="ifd-traffic-card-sub">{formatNumber(detail.rxPacketsPerSecond)} pps</div>
        </div>
      </div>
    </div>

    {/* Byte Rate Chart */}
    {trafficHistory.length > 1 && (
      <div className="ifd-section">
        <h4 className="ifd-section-title"><FaChartLine /> Byte Rate Graph</h4>
        <div className="ifd-chart-container">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={trafficHistory} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tickFormatter={v => formatBitsPerSecond(v)} width={70} tick={{ fontSize: 10 }} />
              <Tooltip content={<TrafficChartTooltip unit="bps" />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                type="monotone" dataKey="txBps" name="TX" dot={false}
                stroke="#e67e22" strokeWidth={2} isAnimationActive={false}
              />
              <Line
                type="monotone" dataKey="rxBps" name="RX" dot={false}
                stroke="#27ae60" strokeWidth={2} isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    )}

    {/* Packet Rate Chart */}
    {trafficHistory.length > 1 && (
      <div className="ifd-section">
        <h4 className="ifd-section-title"><FaTrafficLight /> Packet Rate Graph</h4>
        <div className="ifd-chart-container">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={trafficHistory} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tickFormatter={v => `${formatNumber(v)} pps`} width={70} tick={{ fontSize: 10 }} />
              <Tooltip content={<TrafficChartTooltip unit="pps" />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                type="monotone" dataKey="txPps" name="TX Packets" dot={false}
                stroke="#e67e22" strokeWidth={2} isAnimationActive={false}
              />
              <Line
                type="monotone" dataKey="rxPps" name="RX Packets" dot={false}
                stroke="#27ae60" strokeWidth={2} isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    )}

    {trafficHistory.length <= 1 && (
      <div className="ifd-chart-empty">
        <FaChartLine />
        <span>Enable <strong>Auto</strong> refresh to populate live charts.</span>
      </div>
    )}

    {/* Cumulative Traffic Counters */}
    <div className="ifd-section">
      <h4 className="ifd-section-title"><FaChartLine /> Cumulative Counters</h4>
      <div className="ifd-counter-table">
        <div className="ifd-counter-header">
          <span>Metric</span><span>TX</span><span>RX</span>
        </div>
        <CounterRow label="Bytes" tx={formatBytes(detail.txBytes)} rx={formatBytes(detail.rxBytes)} />
        <CounterRow label="Packets" tx={formatNumber(detail.txPackets)} rx={formatNumber(detail.rxPackets)} />
        <CounterRow
          label="Errors"
          tx={formatNumber(detail.txErrors)} rx={formatNumber(detail.rxErrors)}
          highlight={parseInt(detail.txErrors) > 0 || parseInt(detail.rxErrors) > 0}
        />
        <CounterRow
          label="Drops"
          tx={formatNumber(detail.txDrops)} rx={formatNumber(detail.rxDrops)}
          highlight={parseInt(detail.txDrops) > 0 || parseInt(detail.rxDrops) > 0}
        />
        {detail.txQueueDrops && detail.txQueueDrops !== '0' && (
          <CounterRow label="Queue Drops" tx={formatNumber(detail.txQueueDrops)} rx="—" highlight />
        )}
      </div>
    </div>

    {/* Fast-Path Counters (only if non-zero) */}
    {(parseInt(detail.fpTxBytes) > 0 || parseInt(detail.fpRxBytes) > 0) && (
      <div className="ifd-section">
        <h4 className="ifd-section-title"><FaNetworkWired /> Fast Path Counters</h4>
        <div className="ifd-counter-table">
          <div className="ifd-counter-header">
            <span>Metric</span><span>TX</span><span>RX</span>
          </div>
          <CounterRow label="Bytes" tx={formatBytes(detail.fpTxBytes)} rx={formatBytes(detail.fpRxBytes)} />
          <CounterRow label="Packets" tx={formatNumber(detail.fpTxPackets)} rx={formatNumber(detail.fpRxPackets)} />
        </div>
      </div>
    )}
  </div>
);

// =====================================================================
// ConfigTab — type-specific interface properties not shown elsewhere.
// =====================================================================
const ConfigTab = ({ entries }) => (
  <div className="ifd-tab-content">
    <div className="ifd-section">
      <h4 className="ifd-section-title"><FaCog /> Type-Specific Configuration</h4>
      <div className="ifd-props-grid">
        {entries.map(([key, value]) => (
          <PropRow
            key={key}
            label={formatPropertyLabel(key)}
            value={formatPropertyValue(key, value)}
            mono={isMonoValue(key)}
          />
        ))}
      </div>
    </div>
  </div>
);

// =====================================================================
// ActionsTab — router action buttons for interface management.
// Mirrors the MikroTik WinBox Actions sidebar (Reset Counters, Blink,
// Cable Test). Blink and Cable Test are only available on Ethernet.
// Reset Counters is supported on physical and tunnel interfaces alike.
// =====================================================================

// Interface types that support traffic counter reset via RouterOS API.
// Virtual/loopback interfaces that report counters are included;
// types that don't maintain counters (e.g. none-type) are excluded.
const RESET_COUNTERS_SUPPORTED_TYPES = new Set([
  'ether', 'bridge', 'vlan', 'wg', 'eoip', 'gre', 'ipip',
  'pppoe-out', 'pppoe-in', 'l2tp-out', 'sstp-out', 'ovpn-out',
  'bonding', 'lte', 'loopback'
]);

const ActionsTab = ({
  interfaceType, isEthernet, actionState, cableTestResults,
  onResetCounters, onBlink, onCableTest
}) => {
  // Normalise the type string for comparison.
  const normType = (interfaceType || '').toLowerCase();
  const supportsResetCounters = RESET_COUNTERS_SUPPORTED_TYPES.has(normType);
  const actions = [
    {
      key: 'resetCounters',
      label: 'Reset Traffic Counters',
      description: 'Reset all TX/RX byte, packet, drop, and error counters to zero.',
      icon: <FaUndo />,
      handler: onResetCounters,
      available: supportsResetCounters
    },
    {
      key: 'blink',
      label: 'Blink LED',
      description: 'Flash the port LED to physically identify the interface on the device.',
      icon: <FaBolt />,
      handler: onBlink,
      available: isEthernet
    },
    {
      key: 'cableTest',
      label: 'Cable Test (TDR)',
      description: 'Run a TDR cable test to detect open/short faults and estimate distance.',
      icon: <FaTools />,
      handler: onCableTest,
      available: isEthernet
    }
  ];

  return (
    <div className="ifd-tab-content">
      <div className="ifd-section">
        <h4 className="ifd-section-title"><FaTools /> Interface Actions</h4>
        <div className="ifd-actions-list">
          {actions.filter(a => a.available).map(action => {
            const state = actionState[action.key] || {};
            return (
              <div key={action.key} className="ifd-action-row">
                <div className="ifd-action-info">
                  <span className="ifd-action-label">{action.icon} {action.label}</span>
                  <span className="ifd-action-desc">{action.description}</span>
                </div>
                <div className="ifd-action-controls">
                  {state.message && (
                    <span className="ifd-action-ok"><FaCheckCircle /> {state.message}</span>
                  )}
                  {state.error && (
                    <span className="ifd-action-err"><FaExclamationTriangle /> {state.error}</span>
                  )}
                  <button
                    className="ifd-btn-action"
                    onClick={action.handler}
                    disabled={state.loading}
                    title={action.label}
                  >
                    {state.loading ? <FaSpinner className="spin" /> : action.icon}
                    {state.loading ? 'Running…' : 'Run'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cable test results — shown after a successful cable test */}
      {cableTestResults && Object.keys(cableTestResults).length > 0 && (
        <div className="ifd-section">
          <h4 className="ifd-section-title"><FaTools /> Cable Test Results</h4>
          <div className="ifd-props-grid">
            {Object.entries(cableTestResults)
              .filter(([key]) => !key.startsWith('.'))
              .map(([key, value]) => (
                <PropRow
                  key={key}
                  label={formatPropertyLabel(key)}
                  value={value}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Reusable sub-components
// =====================================================================

/** PropRow — single label/value pair in the properties grid. */
const PropRow = ({ label, value, mono = false }) => (
  <div className="ifd-prop-row">
    <span className="ifd-prop-label">{label}</span>
    <span className={`ifd-prop-value ${mono ? 'mono' : ''}`}>{value || '—'}</span>
  </div>
);

/** CounterRow — TX/RX counter row with optional error highlighting. */
const CounterRow = ({ label, tx, rx, highlight = false }) => (
  <div className={`ifd-counter-row ${highlight ? 'ifd-counter-row--highlight' : ''}`}>
    <span className="ifd-counter-label">{label}</span>
    <span className="ifd-counter-value mono">{tx}</span>
    <span className="ifd-counter-value mono">{rx}</span>
  </div>
);

// =====================================================================
// Value formatting helpers for type-specific properties
// =====================================================================

const isMonoValue = (key) => new Set([
  'mac-address', 'orig-mac-address', 'remote-address', 'local-address',
  'speed', 'rate', 'mtu', 'actual-mtu', 'l2mtu', 'max-l2mtu',
  'listen-port', 'vlan-id', 'tunnel-id', 'bandwidth'
]).has(key);

const formatPropertyValue = (key, value) => {
  if (value === 'true') return <span className="ifd-value--true"><FaCheckCircle /> Yes</span>;
  if (value === 'false') return <span className="ifd-value--false"><FaTimesCircle /> No</span>;
  return value || '—';
};

export default InterfaceDetailModal;
